import {
    Badge,
    BlockStack,
    DataTable,
    Link,
    Typography,
    type DataTableColumn,
} from '@pv-frontend/ui-components-web';
import type { VaultListFilterView } from './vault-list-filter';

/**
 * Plain view model for one vault-search result — decoupled from Relay's generated
 * types so this table is unit-testable without a Relay harness. The screen maps
 * `vaultSearch` results into it.
 */
export interface VaultView {
    vaultId: string;
    name: string;
    identifier: string;
    jurisdiction: string;
    ownerEmail: string | null;
    planName: string | null;
    subscriptionStatus: string | null;
    /** Whether the vault's Pro is a live complimentary grant rather than a purchase. */
    isComplimentary: boolean;
    /** When the grant ends (ISO-8601). Null for an indefinite grant — and for every vault not comped. */
    complimentaryUntil: string | null;
    /** The operator who granted it, when comped. ⛔ Operator-only: never surface outside this console. */
    grantedBy: string | null;
}

const UNTIL_FORMAT = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
});

/**
 * The term end of a complimentary grant as a UTC date, the same for every operator. Only meaningful for
 * a comped vault, where a null end is an indefinite grant (never a far-future date). An unparseable value
 * falls back to the raw string rather than "Invalid Date".
 */
export function formatComplimentaryUntil(iso: string | null): string {
    if (iso === null) return 'Indefinite';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : UNTIL_FORMAT.format(date);
}

/**
 * Client-side refinement over the server results: matches the query against name,
 * identifier and owner email so the list narrows instantly while the debounced
 * server search catches up. Exported for direct unit testing.
 */
export function filterVaults(vaults: readonly VaultView[], search: string): VaultView[] {
    const term = search.trim().toLowerCase();
    if (!term) return [...vaults];
    return vaults.filter((vault) =>
        [vault.name, vault.identifier, vault.ownerEmail ?? '']
            .some((field) => field.toLowerCase().includes(term)),
    );
}

const columns: DataTableColumn<VaultView>[] = [
    {
        key: 'name',
        header: 'Vault',
        render: (vault) => (
            <BlockStack gap="050">
                <Link href={`/vaults/${vault.vaultId}`}>{vault.name}</Link>
                <Typography variant="body-sm" tone="text-secondary">
                    {vault.identifier}
                </Typography>
            </BlockStack>
        ),
    },
    {
        key: 'owner',
        header: 'Owner',
        render: (vault) =>
            vault.ownerEmail ? (
                <Typography variant="body-sm">{vault.ownerEmail}</Typography>
            ) : (
                <Typography variant="body-sm" tone="text-secondary">
                    —
                </Typography>
            ),
    },
    {
        key: 'jurisdiction',
        header: 'Jurisdiction',
        render: (vault) => <Typography variant="body-sm">{vault.jurisdiction}</Typography>,
    },
    {
        key: 'plan',
        header: 'Plan',
        align: 'end',
        render: (vault) =>
            vault.planName ? (
                <Badge tone={vault.subscriptionStatus === 'Active' ? 'success' : 'neutral'}>
                    {vault.planName}
                </Badge>
            ) : (
                <Badge tone="neutral">Free</Badge>
            ),
    },
];

const complimentaryColumns: DataTableColumn<VaultView>[] = [
    {
        key: 'complimentaryUntil',
        header: 'Complimentary until',
        render: (vault) => (
            <Typography variant="body-sm">{formatComplimentaryUntil(vault.complimentaryUntil)}</Typography>
        ),
    },
    {
        key: 'grantedBy',
        header: 'Granted by',
        render: (vault) =>
            vault.grantedBy ? (
                <Typography variant="body-sm">{vault.grantedBy}</Typography>
            ) : (
                <Typography variant="body-sm" tone="text-secondary">
                    Not recorded
                </Typography>
            ),
    },
];

// The complimentary list keeps the directory's columns, with the grant facts before the plan badge.
const complimentaryListColumns = [...columns.slice(0, -1), ...complimentaryColumns, ...columns.slice(-1)];

export interface VaultTableProps {
    vaults: readonly VaultView[];
    /** Live search term; filters the rows by name / identifier / owner email. */
    search: string;
    /** Which list this is. The complimentary list adds each grant's term end and granting operator. */
    filter?: VaultListFilterView;
}

function emptyStateFor(filter: VaultListFilterView, search: string): string {
    const term = search.trim();
    if (filter === 'complimentary') {
        return term
            ? `No complimentary vaults match "${term}".`
            : 'No vaults have complimentary Pro right now.';
    }
    return term ? `No vaults match "${term}".` : 'No vaults found.';
}

/** Searchable, read-only vault list rendered as an accessible data table. */
export function VaultTable({ vaults, search, filter = 'all' }: VaultTableProps) {
    const rows = filterVaults(vaults, search);

    return (
        <DataTable
            columns={filter === 'complimentary' ? complimentaryListColumns : columns}
            rows={rows}
            keyExtractor={(vault) => vault.vaultId}
            emptyState={emptyStateFor(filter, search)}
        />
    );
}
